import { useState, useCallback, useRef, useEffect } from 'react'
import { Loader2, Truck, Package, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { adminOrderApi } from '@/api/admin-order'
import { settingsApi } from '@/api/settings'
import { showError } from '@/lib/error'
import { useOrderCreateStore } from '@/stores/order-create'

export function ConfirmStep() {
  const {
    selectedUser, cartItems, bundleItems,
    selectedAddress, manualAddress, useManualAddress,
    shippingType, setShippingType,
    mark, setMark,
    isStore, setStore,
    isCash, setCash,
    shippingPartial, setShippingPartial,
    shippingRemark, setShippingRemark,
    setCreatedOrderId, setCreatedPayPrice, setPaymentQrcodeInfo,
    prevStep,
  } = useOrderCreateStore()

  const [isCreating, setIsCreating] = useState(false)
  const [postage, setPostage] = useState(0)
  const [maxTotalPrice, setMaxTotalPrice] = useState<number | null>(null)
  const creatingRef = useRef(false)

  useEffect(() => {
    settingsApi.getSetting('order.max_total_price')
      .then((s) => {
        const val = parseFloat(s.value)
        setMaxTotalPrice(isNaN(val) ? null : val)
      })
      .catch(() => setMaxTotalPrice(null))
  }, [])

  const bundleTotal = bundleItems.reduce((sum, item) => sum + item.promotionPrice * item.quantity, 0)
  const cartTotal = cartItems.reduce((sum, item) => sum + item.price * item.cartNum, 0)
  const goodsTotal = bundleTotal + cartTotal
  const totalPrice = goodsTotal + postage
  const exceedsLimit = maxTotalPrice !== null && maxTotalPrice > 0 && goodsTotal > maxTotalPrice

  // 获取收货信息
  const addressInfo = useManualAddress
    ? {
        realName: manualAddress?.real_name || '',
        phone: manualAddress?.phone || '',
        address: `${manualAddress?.province || ''}${manualAddress?.city || ''}${manualAddress?.district || ''}${manualAddress?.detail || ''}`,
      }
    : { realName: selectedAddress?.real_name || '', phone: selectedAddress?.phone || '', address: `${selectedAddress?.province || ''}${selectedAddress?.city || ''}${selectedAddress?.district || ''}${selectedAddress?.detail || ''}` }

  const addressId = useManualAddress ? 0 : (selectedAddress?.id || 0)

  const {
    province: manualProvince,
    city: manualCity,
    district: manualDistrict,
    city_id: manualCityId,
    detail: manualDetail,
    save_as_common: manualSaveAsCommon,
  } = manualAddress || {}

  // 创建订单
  const handleCreateOrder = useCallback(async () => {
    if (creatingRef.current) return
    if (!selectedUser) {
      toast.error('请先选择用户')
      return
    }
    if (cartItems.length === 0 && bundleItems.length === 0) {
      toast.error('购物车为空')
      return
    }

    creatingRef.current = true
    setIsCreating(true)
    try {
      const result = await adminOrderApi.create({
        uid: selectedUser.uid,
        items: cartItems.map(item => ({
          product_id: item.productId,
          sku_unique: item.unique,
          quantity: item.cartNum,
        })),
        bundle_items: bundleItems.map(item => ({
          bundle_id: item.bundleId,
          quantity: item.quantity,
        })),
        address_id: addressId,
        real_name: addressInfo.realName,
        phone: addressInfo.phone,
        address: addressInfo.address,
        province: manualProvince,
        city: manualCity,
        district: manualDistrict,
        city_id: manualCityId,
        detail: manualDetail,
        save_as_common_address: manualSaveAsCommon,
        shipping_type: shippingType,
        postage: postage,
        mark: mark,
        is_store: isStore,
        is_cash: isCash,
        shipping_partial: shippingPartial,
        shipping_remark: shippingRemark,
      })

      setCreatedOrderId(result.order_id)
      setCreatedPayPrice(result.pay_price)
      setPaymentQrcodeInfo({
        status: result.payment_qrcode_status,
        qrcode: result.payment_qrcode,
        departmentId: result.payment_department_id,
        departmentName: result.payment_department_name,
      })
      toast.success('订单创建成功')
    } catch (error) {
      showError(error, '创建订单失败')
    } finally {
      creatingRef.current = false
      setIsCreating(false)
    }
  }, [selectedUser, cartItems, bundleItems, addressId, addressInfo.realName, addressInfo.phone, addressInfo.address, manualProvince, manualCity, manualDistrict, manualCityId, manualDetail, manualSaveAsCommon, shippingType, postage, mark, isStore, isCash, shippingPartial, shippingRemark, setCreatedOrderId, setCreatedPayPrice, setPaymentQrcodeInfo])

  return (
    <div className="space-y-4">
      {/* 收货信息 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">收货信息</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">收货人:</span>
            <span className="font-medium">{addressInfo.realName}</span>
            <span className="text-muted-foreground ml-4">电话:</span>
            <span className="font-medium">{addressInfo.phone}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">地址:</span>
            <span>{addressInfo.address}</span>
          </div>
        </CardContent>
      </Card>

      {/* 商品清单 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">商品清单</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {bundleItems.map((item) => (
            <div key={item.bundleId} className="flex items-start gap-3 py-2">
              <div className="flex h-10 w-10 items-center justify-center rounded bg-muted text-xs font-medium text-muted-foreground">
                组合
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.bundleName}</p>
                <p className="text-xs text-muted-foreground">
                  促销组合 x {item.quantity}
                </p>
                {item.items && item.items.length > 0 && (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {item.items
                      .slice(0, 3)
                      .map((bundleItem) => `${bundleItem.product_name_snapshot}${bundleItem.sku_name_snapshot ? `/${bundleItem.sku_name_snapshot}` : ''} x${bundleItem.quantity}`)
                      .join('、')}
                    {item.items.length > 3 ? ` 等 ${item.items.length} 件` : ''}
                  </p>
                )}
              </div>
              <span className="text-sm font-medium text-red-600">¥{(item.promotionPrice * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          {cartItems.map((item, index) => (
            <div key={`${item.unique}-${index}`} className="flex items-center gap-3 py-2">
              <img src={item.productImage} alt={item.productName} className="h-10 w-10 rounded object-cover" />
              <div className="flex-1">
                <p className="text-sm font-medium">{item.productName}</p>
                <p className="text-xs text-muted-foreground">{item.suk || '默认规格'} x {item.cartNum}</p>
              </div>
              <span className="text-sm font-medium text-red-600">¥{(item.price * item.cartNum).toFixed(2)}</span>
            </div>
          ))}
          <Separator />
          <div className="flex justify-end text-sm">
            <span className="text-muted-foreground">商品合计: </span>
            <span className="font-bold text-red-600 ml-1">¥{goodsTotal.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      {/* 配送方式 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">配送方式</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={shippingType.toString()}
            onValueChange={(val) => setShippingType(Number(val))}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="1" />
              <Label className="flex items-center gap-1 cursor-pointer">
                <Truck className="h-4 w-4" />
                快递配送
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="2" />
              <Label className="flex items-center gap-1 cursor-pointer">
                <Package className="h-4 w-4" />
                到店自提
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* 邮费和备注 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">其他设置</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>邮费</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={postage}
              onChange={(e) => setPostage(parseFloat(e.target.value) || 0)}
              placeholder="输入邮费金额"
            />
          </div>
          <div className="space-y-2">
            <Label>订单备注</Label>
            <Textarea
              placeholder="输入订单备注（可选）"
              value={mark}
              onChange={(e) => setMark(e.target.value)}
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* 订单标记 */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">订单标记</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={isStore} onCheckedChange={setStore} />
              <Label className="text-sm">仓库保管</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isCash} onCheckedChange={setCash} />
              <Label className="text-sm">货到付款</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={shippingPartial} onCheckedChange={setShippingPartial} />
              <Label className="text-sm">部分发货</Label>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">发货备注</Label>
            <Textarea
              value={shippingRemark}
              onChange={(e) => setShippingRemark(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="发货备注（可选）..."
            />
          </div>
        </CardContent>
      </Card>

      {/* 价格明细 */}
      <Card className="border-primary">
        <CardHeader>
          <CardTitle className="text-base">价格明细</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">商品总价</span>
            <span>¥{goodsTotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">邮费</span>
            <span>¥{postage.toFixed(2)}</span>
          </div>
          <Separator />
          <div className="flex justify-between text-lg font-bold">
            <span>应付金额</span>
            <span className="text-red-600">¥{totalPrice.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      {/* 提示 */}
      <div className="flex items-start gap-2 p-3 bg-muted rounded-lg text-sm">
        <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
        <span className="text-muted-foreground">点击"创建订单"将直接创建订单（线下支付），最终价格以服务端计算为准</span>
      </div>

      {exceedsLimit && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            订单商品结算总价 ¥{goodsTotal.toFixed(2)}，超过最大限制 ¥{maxTotalPrice.toFixed(2)}，无法创建订单。
          </AlertDescription>
        </Alert>
      )}

      {/* 底部操作栏 */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={prevStep}>上一步</Button>
        <Button onClick={handleCreateOrder} disabled={exceedsLimit || isCreating}>
          {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          创建订单
        </Button>
      </div>
    </div>
  )
}
